/*****************************************************************************
 *   © Copyright 2021   Quantitative Software Management, Inc.               *
 *              --  All rights reserved.  --                                 *
 *   Unpublished -- rights reserved under the Copyright Laws                 *
 *   of the United States.                                                   *
 *                                                                           *
 *   This computer software is protected by trade secret, copyright law,     *
 *   and international treaties and contains proprietary and confidential    *
 *   trade secrets of Quantitative Software Management, Inc.  Unauthorized   *
 *   use, reproduction, or distribution of this program or any portion of    *
 *   it will result in severe civil or criminal penalties and will be        *
 *   prosecuted to the full extent of the law.                               *
 *                                                                           *
 *   Use, duplication, or disclosure by the Government is subject to         *
 *   restrictions as set forth in                                            *
 *      (i) a license agreement incorporated in the contract,                *
 *      (ii) the definition of "restricted rights" in paragraph (c)(1)(ii)   *
 *           of DFARS 252.227-7013 (1988), and                               *
 *      (iii) the definition of "restricted rights" in paragraph (a) of      *
 *            FAR 52.227-14, Alt III (Jun 1987) or                           *
 *      (iv) FAR 52.227-19 (Jun 1987), as applicable.                        *
 *   For purposes of the FAR, this software shall be deemed to               *
 *   be "unpublished" and licensed with disclosure prohibitions.             *
 *   Contractor/Manufacturer is   Quantitative Software Management, Inc.     *
 *                                                                           *
 *****************************************************************************/

import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';

import { BasicSolution } from '../Models/BasicSolution';
import { FuncUnitInfo } from '../Models/ChangedFunctionUnit';
import { ApplicationDomain, ExtendedGenericData, GenericData, IndustrySector, MonetaryUnit } from '../Models/Generic-Data';
import { MetricInfo } from '../Models/MetricInfo';
import { ObsNode } from '../Models/ObsNode';
import { SizeDetailInfo } from '../Models/SizeDetailInfo';
import { TrendId } from '../Models/TrendId';
import { UserView } from '../Models/UserView';
import { UtilityService } from './Utility.Service';

@Injectable({
  providedIn: 'root'
})
export class LookupService {
  constructor(private http: HttpClient, private util: UtilityService) { }

  public AllProjectManagers(obsNodeId: number | null, includeHigherNodes: boolean): Observable<Array<GenericData>> {
    if (obsNodeId !== null) {
      return this.http.get<Array<GenericData>>('api/lookup/projectandobsmanagers/' + obsNodeId + '/' + includeHigherNodes, this.util.AjaxOptions());
    }
    return this.http.get<Array<GenericData>>('api/lookup/projectmanagers', this.util.AjaxOptions());
  }

  public AvailableUsers(projId: number | null = null, stageId: number | null = null): Observable<Array<GenericData>> {
    return this.http.get<Array<GenericData>>('api/lookup/availableusers/' + projId + '/' + stageId, this.util.AjaxOptions());
  }

  public ObsNodes(): Observable<Array<ObsNode>> {
    return this.http.get<Array<ObsNode>>('api/lookup/obstree', this.util.AjaxOptions());
  }

  public SolutionMethods(): Observable<Array<GenericData>> {
    return this.http.get<Array<GenericData>>('api/lookup/solutionmethods', this.util.AjaxOptions());
  }

  public AccessLevels(): Observable<Array<GenericData>> {
    return this.http.get<Array<GenericData>>('api/lookup/accesslevels', this.util.AjaxOptions());
  }

  public ProductivityRanks(esloc: number, trendconfig: TrendId): Observable<Array<ExtendedGenericData>> {
    return this.http.get<Array<ExtendedGenericData>>('api/lookup/productivityranks/' + esloc + '/' + trendconfig.trendGroupId + '/' + trendconfig.trendVersionId, this.util.AjaxOptions());
  }

  public GetPhaseNames(): Observable<Array<GenericData>> {
    return this.http.get<Array<GenericData>>('api/lookup/PhaseNames', this.util.AjaxOptions());
  }

  public GetShapes(): Observable<Array<GenericData>> {
    return this.http.get<Array<GenericData>>('api/lookup/PhaseShapes', this.util.AjaxOptions());
  }

  public FunctionUnits(): Observable<Array<FuncUnitInfo>> {
    return this.http.get<Array<FuncUnitInfo>>('api/lookup/functionunits', this.util.AjaxOptions());
  }

  public DevMethods(): Observable<Array<GenericData>> {
    return this.http.get<Array<GenericData>>('api/lookup/devmethods', this.util.AjaxOptions());
  }

  public DevClasses(): Observable<Array<GenericData>> {
    return this.http.get<Array<GenericData>>('api/lookup/devclasses', this.util.AjaxOptions());
  }

  public Keywords(): Observable<Array<GenericData>> {
    return this.http.get<Array<GenericData>>('api/lookup/keywords', this.util.AjaxOptions());
  }

  public Levels(): Observable<Array<GenericData>> {
    return this.http.get<Array<GenericData>>('api/lookup/levels', this.util.AjaxOptions());
  }

  public Languages(): Observable<Array<GenericData>> {
    return this.http.get<Array<GenericData>>('api/lookup/languages', this.util.AjaxOptions());
  }

  public SizeBins(trendconfig: TrendId): Observable<Array<ExtendedGenericData>> {
    return this.http.get<Array<ExtendedGenericData>>('api/lookup/sizebins/' + trendconfig.trendGroupId + '/' + trendconfig.trendVersionId, this.util.AjaxOptions());
  }

  public IndustrySectors(): Observable<Array<IndustrySector>> {
    return this.http.get<Array<IndustrySector>>('api/lookup/IndustrySectors', this.util.AjaxOptions());
  }

  public ApplicationDomains(): Observable<Array<ApplicationDomain>> {
    return this.http.get<Array<ApplicationDomain>>('api/lookup/ApplicationDomains', this.util.AjaxOptions());
  }

  public LoggedSolutions(projectid: number, stageid: number, includeCurrent: boolean): Observable<Array<BasicSolution>> {
    return this.http.get<Array<BasicSolution>>('api/lookup/loggedsolutions/' + projectid + '/' + stageid + '/' + includeCurrent, this.util.AjaxOptions());
  }

  public ProjectFilters(): Observable<Array<GenericData>> {
    return this.http.get<Array<GenericData>>('api/lookup/projectfilters', this.util.AjaxOptions());
  }

  public States(): Observable<Array<GenericData>> {
    return this.http.get<Array<GenericData>>('api/lookup/states', this.util.AjaxOptions());
  }

  public Statuses(): Observable<Array<GenericData>> {
    return this.http.get<Array<GenericData>>('api/lookup/statuses', this.util.AjaxOptions());
  }

  public MonetaryUnits(): Observable<Array<MonetaryUnit>> {
    return this.http.get<Array<MonetaryUnit>>('api/lookup/monetaryunits', this.util.AjaxOptions());
  }

  public ExternalCalls(): Observable<Array<GenericData>> {
    return this.http.get<Array<GenericData>>('api/lookup/externalcalls', this.util.AjaxOptions());
  }

  public SkillsConfigured(): Observable<boolean> {
    //console.log('checking for skills in lookup service');
    return this.http.get<boolean>('api/lookup/SkillsAreEnabled', this.util.AjaxOptions());
  }

  public SkillsTooltip(): Observable<string> {
    return this.http.get('api/lookup/skillstooltip', { responseType: 'text' });
  }

  public GetUserView(): Observable<UserView> {
    return this.http.get<UserView>('api/lookup/getuserview', this.util.AjaxOptions());
    
  }

  public GetSizingTemplate(projectId): Observable<GenericData[]> {
    return this.http.get<GenericData[]>('api/lookup/GetSizingTemplates/' + projectId, this.util.AjaxOptions());
  }

  public GetSizingConfiguration(configId): Observable<SizeDetailInfo[]> {
    return this.http.get<SizeDetailInfo[]>('api/lookup/GetSizingConfiguration/' + configId, this.util.AjaxOptions());
  }
  public GetContingencyMetrics(): Observable<MetricInfo[]> {
    return this.http.get<MetricInfo[]>('api/lookup/getcontingencymetrics', this.util.AjaxOptions());
  }
}
